/* ============================================================
   Trillobeat — Motion FX v8
   ONE unified wave: a glow band travels down the viewport and
   sequentially lights headings and photos in document order —
   heading -> photo -> next heading -> photo. Runs on desktop and
   mobile; on desktop hovering an image hands control to the cursor
   spotlight for that image while the wave continues elsewhere.
   Keeps v7 behaviours: touch-scroll fade + restart, clip rects,
   horizontal-carousel skip, word reveal, scroll reveals.
   ============================================================ */
(function () {
  var root = document.documentElement;
  if (!root.classList.contains('tb-motion')) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
  var isWave = root.classList.contains('tb-wave'); /* wave runs: conductor + heading glow */
  var isWaveFull = root.classList.contains('tb-wave-full'); /* + photo holograms (our-process / our-story) */
  var lastX = -9999, lastY = -9999, isMobileScroll = 0, scrollTimer = 0;

  if (canHover) document.addEventListener('mousemove', function (e) {
    lastX = e.clientX; lastY = e.clientY;
  }, { passive: true });

  function hideDuringScroll() {
    if (canHover) return;
    isMobileScroll = 1;
    root.classList.add('tb-touch-scrolling');
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(function () {
      isMobileScroll = 0;
      root.classList.remove('tb-touch-scrolling');
      window.dispatchEvent(new CustomEvent('tb:holo-scroll-stop'));
    }, 140);
  }
  if (!canHover) {
    window.addEventListener('touchstart', hideDuringScroll, { passive: true });
    window.addEventListener('touchmove', hideDuringScroll, { passive: true });
    window.addEventListener('scroll', hideDuringScroll, { passive: true });
  }

  function vars(el, x, y, rx, ry) {
    el.style.setProperty('--tb-x', x + 'px');
    el.style.setProperty('--tb-y', y + 'px');
    el.style.setProperty('--tb-rx', rx + 'px');
    el.style.setProperty('--tb-ry', ry + 'px');
  }
  function ease(p) { return -(Math.cos(Math.PI * p) - 1) / 2; }

  function isClipperCS(cs, el) {
    return /(auto|scroll|hidden|clip)/.test(cs.overflowX + cs.overflowY) ||
      el.scrollWidth > el.clientWidth + 4;
  }
  function collectClippers(img) {
    var list = [], el = img.parentElement;
    while (el && el !== document.body) {
      if (isClipperCS(getComputedStyle(el), el)) list.push(el);
      el = el.parentElement;
    }
    return list;
  }
  function inHorizontalCarousel(img) {
    var el = img.parentElement;
    while (el && el !== document.body) {
      var cls = (el.className || '').toString();
      var horizontal = el.scrollWidth > el.clientWidth + 8;
      var named = /(carousel|slider|scroll|gallery|multi-column|media-grid|image-list)/i.test(cls);
      var scrollish = /(auto|scroll|hidden|clip)/.test(getComputedStyle(el).overflowX);
      if (horizontal && (named || scrollish)) return true;
      el = el.parentElement;
    }
    return false;
  }

  /* ============================================================
     Unified wave conductor
     ============================================================ */
  var waveTargets = [];
  var WAVE_MS = 6800, PAUSE_MS = 1500;
  var waveStart = null, pausedUntil = 0, quickStart = 0;

  function waveBandH() {
    var vh = innerHeight || root.clientHeight;
    return Math.max(170, Math.min(380, vh * 0.26));
  }

  function conductor(ts) {
    requestAnimationFrame(conductor);
    if (document.hidden) return;
    if (isMobileScroll) { waveStart = null; return; } /* CSS hides; cycle restarts after scroll */
    if (ts < pausedUntil) return;
    if (waveStart === null) waveStart = ts;
    var p = (ts - waveStart) / WAVE_MS;
    if (p >= 1) {
      waveTargets.forEach(function (t) { t.off(); });
      waveStart = null;
      quickStart = 0; /* only the first post-scroll pass skips the lead-in */
      pausedUntil = ts + PAUSE_MS;
      return;
    }
    var vh = innerHeight || root.clientHeight;
    var b = waveBandH();
    /* after a touch scroll the wave starts right at the top edge of the
       screen instead of approaching from above it */
    var y0 = quickStart ? 0 : -b;
    var yLine = y0 + ease(p) * (vh + b - y0);
    waveTargets.forEach(function (t) {
      if (t.skip && t.skip()) return; /* cursor override — leave as is */
      t.band(yLine, b);
    });
  }
  if (isWave) requestAnimationFrame(conductor);
  window.addEventListener('tb:holo-scroll-stop', function () {
    waveStart = null; pausedUntil = 0; quickStart = 1;
  });

  /* ============================================================
     PART A — main-page (Our Process / Our Story)
     ============================================================ */
  function setupPage() {
    var section = document.querySelector('.shopify-section--main-page');
    if (!section) return;

    var h1 = section.querySelector('.section-header h1');
    if (h1 && !h1.dataset.tbWords) {
      h1.dataset.tbWords = '1';
      var ws = h1.textContent.trim().split(/\s+/);
      h1.textContent = '';
      ws.forEach(function (w, i) {
        var s = document.createElement('span');
        s.className = 'tb-word';
        s.textContent = w;
        s.style.animationDelay = (0.16 + i * 0.085) + 's';
        h1.appendChild(s);
        if (i < ws.length - 1) h1.appendChild(document.createTextNode(' '));
      });
    }

    var prose = section.querySelector('.prose');
    if (isWave && prose) {
      if (isWaveFull) {
        Array.prototype.forEach.call(prose.querySelectorAll('img'), attachPhoto);
      }
      /* text wave: page h1 + step headings inside prose */
      if (h1) attachHeading(h1);
      Array.prototype.forEach.call(prose.querySelectorAll('h2, h3'), attachHeading);
    }
  }

  /* ---------- generic text reveals on all content pages ---------- */
  function setupReveals() {
    var els = document.querySelectorAll('#main .prose > *, #main .section-header');
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('tb-inview'); });
      return;
    }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('tb-inview'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    Array.prototype.forEach.call(els, function (el) {
      if (el.classList.contains('tb-inview')) return;
      /* items inside horizontal carousels reveal instantly (no jank) */
      if (inHorizontalCarousel(el)) { el.classList.add('tb-inview'); return; }
      io.observe(el);
    });
  }

  /* ---------- photos: fixed overlay + wave + cursor override ---------- */
  function attachPhoto(img) {
    if (!img || img.dataset.tbHoloAttached || inHorizontalCarousel(img)) return;
    img.dataset.tbHoloAttached = '1';

    var overlay = document.createElement('div');
    var clone = document.createElement('img');
    var ready = 0, hovered = 0;
    var clippers = collectClippers(img);

    overlay.className = 'tb-holo-abs';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.appendChild(clone);
    document.body.appendChild(overlay);

    function src() {
      if (ready) return;
      var s = img.currentSrc || img.src;
      if (!s) return;
      if (img.srcset) clone.srcset = img.srcset;
      if (img.sizes) clone.sizes = img.sizes;
      clone.src = s;
      ready = 1;
    }
    function clipRect() {
      var r = img.getBoundingClientRect();
      var left = r.left + 2, top = r.top + 2, right = r.right - 2, bottom = r.bottom - 2;
      for (var i = 0; i < clippers.length; i++) {
        var cr = clippers[i].getBoundingClientRect();
        if (cr.left + 2 > left) left = cr.left + 2;
        if (cr.right - 2 < right) right = cr.right - 2;
        if (cr.top + 2 > top) top = cr.top + 2;
        if (cr.bottom - 2 < bottom) bottom = cr.bottom - 2;
      }
      if (left < 0) left = 0;
      if (right > innerWidth) right = innerWidth;
      return {
        left: left, top: top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
        originLeft: r.left + 2, originTop: r.top + 2, raw: r
      };
    }
    function place() {
      var c = clipRect();
      overlay.style.top = c.top + 'px';
      overlay.style.left = c.left + 'px';
      overlay.style.width = c.width + 'px';
      overlay.style.height = c.height + 'px';
      overlay.style.borderRadius = getComputedStyle(img).borderRadius;
      clone.style.width = c.raw.width + 'px';
      clone.style.height = c.raw.height + 'px';
      clone.style.transform = 'translate(' + (c.originLeft - c.left) + 'px,' + (c.originTop - c.top) + 'px)';
      return c;
    }

    if (canHover) {
      var over = 0, raf = 0, cx = 0, cy = 0, first = 1;
      function loop() {
        if (!over) { raf = 0; return; }
        var c = place();
        var tx = lastX - c.originLeft, ty = lastY - c.originTop;
        if (first) { cx = tx; cy = ty; first = 0; }
        cx += (tx - cx) * 0.14;
        cy += (ty - cy) * 0.14;
        vars(overlay,
          Math.min(Math.max(cx + (c.originLeft - c.left), 0), c.width),
          Math.min(Math.max(cy + (c.originTop - c.top), 0), c.height),
          190, 190);
        raf = requestAnimationFrame(loop);
      }
      img.addEventListener('mouseenter', function () {
        over = 1; hovered = 1; first = 1;
        src(); place();
        overlay.classList.add('tb-on');
        if (!raf) raf = requestAnimationFrame(loop);
      });
      img.addEventListener('mouseleave', function () {
        over = 0; hovered = 0;
        overlay.classList.remove('tb-on');
      });
    }

    waveTargets.push({
      skip: function () { return hovered; },
      off: function () { if (!hovered) overlay.classList.remove('tb-on'); },
      band: function (yLine, b) {
        var r = img.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) { overlay.classList.remove('tb-on'); return; }
        var localY = yLine - r.top;
        if (localY > -b && localY < r.height + b) {
          src();
          var c = place();
          overlay.classList.add('tb-on');
          vars(overlay, c.width / 2, yLine - c.top, c.width * 0.98, b);
        } else {
          overlay.classList.remove('tb-on');
        }
      }
    });
  }

  /* ---------- headings: glowing text overlay + wave ---------- */
  function attachHeading(h) {
    if (!h || h.dataset.tbTextHolo) return;
    h.dataset.tbTextHolo = '1';

    var overlay = document.createElement('div');
    overlay.className = 'tb-holo-text';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);

    function syncStyle() {
      var cs = getComputedStyle(h);
      overlay.style.font = cs.font;
      overlay.style.letterSpacing = cs.letterSpacing;
      overlay.style.wordSpacing = cs.wordSpacing;
      overlay.style.textTransform = cs.textTransform;
      overlay.style.textAlign = cs.textAlign;
      overlay.style.textIndent = cs.textIndent;
      overlay.style.padding = cs.padding;
      overlay.style.boxSizing = cs.boxSizing;
      overlay.textContent = h.textContent;
    }
    syncStyle();
    window.addEventListener('resize', syncStyle, { passive: true });
    setTimeout(syncStyle, 600);
    setTimeout(syncStyle, 1500);

    function place() {
      var r = h.getBoundingClientRect();
      overlay.style.top = r.top + 'px';
      overlay.style.left = r.left + 'px';
      overlay.style.width = r.width + 'px';
      overlay.style.height = r.height + 'px';
      return r;
    }

    waveTargets.push({
      skip: null,
      off: function () { overlay.classList.remove('tb-on'); },
      band: function (yLine, b) {
        var r = h.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) { overlay.classList.remove('tb-on'); return; }
        var localY = yLine - r.top;
        if (localY > -b && localY < r.height + b) {
          place();
          overlay.classList.add('tb-on');
          vars(overlay, r.width / 2, localY, r.width * 0.9, b);
        } else {
          overlay.classList.remove('tb-on');
        }
      }
    });
  }

  /* ---------- hero / banner boxes: in-place clone + wave ---------- */
  function clonePicture(box) {
    var pic = box && box.querySelector('picture');
    if (!pic || box.querySelector('.tb-holo-clone')) return null;
    var c = pic.cloneNode(true);
    c.className = 'tb-holo-clone';
    c.setAttribute('aria-hidden', 'true');
    var im = c.querySelector('img');
    if (im) {
      im.removeAttribute('id');
      im.removeAttribute('fetchpriority');
      im.setAttribute('loading', 'lazy');
    }
    pic.parentNode.insertBefore(c, pic.nextSibling);
    return c;
  }

  function attachBox(box, radius) {
    if (!box || box.dataset.tbBoxAttached) return;
    box.dataset.tbBoxAttached = '1';
    var hovered = 0;

    if (canHover) {
      var over = 0, raf = 0, cx = 0, cy = 0, first = 1;
      function loop() {
        if (!over) { raf = 0; return; }
        var r = box.getBoundingClientRect();
        var tx = lastX - r.left, ty = lastY - r.top;
        if (first) { cx = tx; cy = ty; first = 0; }
        cx += (tx - cx) * 0.14;
        cy += (ty - cy) * 0.14;
        vars(box, cx, cy, radius, radius);
        raf = requestAnimationFrame(loop);
      }
      box.addEventListener('mouseenter', function () {
        over = 1; hovered = 1; first = 1;
        box.classList.add('tb-holo-active');
        if (!raf) raf = requestAnimationFrame(loop);
      });
      box.addEventListener('mouseleave', function () {
        over = 0; hovered = 0;
        box.classList.remove('tb-holo-active');
      });
    }

    waveTargets.push({
      skip: function () { return hovered; },
      off: function () { if (!hovered) box.classList.remove('tb-holo-active'); },
      band: function (yLine, b) {
        var r = box.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) { box.classList.remove('tb-holo-active'); return; }
        var localY = yLine - r.top;
        if (localY > -b && localY < r.height + b) {
          box.classList.add('tb-holo-active');
          vars(box, r.width / 2, localY, r.width * 0.98, b);
        } else {
          box.classList.remove('tb-holo-active');
        }
      }
    });
  }

  function setupHero() {
    var hero = document.querySelector('.shopify-section--slideshow slideshow-carousel.slideshow');
    if (!hero) return;
    var made = 0;
    hero.querySelectorAll('.slideshow__slide .content-over-media').forEach(function (box) {
      if (clonePicture(box) || box.querySelector('.tb-holo-clone')) made++;
    });
    if (!made) return;
    attachBox(hero, 235);
  }

  function setupCollectionBanner() {
    var box = document.querySelector('.shopify-section--collection-banner .content-over-media');
    if (!box) return;
    if (!clonePicture(box) && !box.querySelector('.tb-holo-clone')) return;
    attachBox(box, Math.round(Math.min(250, Math.max(135, box.clientWidth * 0.13))));
  }

  function init() {
    setupReveals();
    setupPage();
    if (isWave) {
      setupHero();
      setupCollectionBanner();
    }
  }
  if (document.readyState === 'complete') init();
  else window.addEventListener('load', init);
})();
